import { create } from 'zustand';
import type { IOperatorAlertV1 } from '@/lib/alerts/alert.types';
import { useAlertsStore } from '@/lib/alerts/alerts.store';
import { DIALOG_MAX_AGE_MS, GROUP_MS, SEVERITY_RANK } from '@/lib/alerts/delivery.policy';
import { usePresenceStore } from '@/state/presence.store';

// The OS notification of an operator alert (docs/verticals/operator-notifications.md §1a
// decision 3, §7.5 «away … OS notification (M2, opt-in)», slice U2).
//
//   · OPT-IN, explicit: the bell's toggle. The browser is asked (`requestPermission`) on
//     that click and nowhere else — never on load. The choice is kept per browser
//     (localStorage, try/catch: a locked-down profile just forgets it).
//   · ONLY while the tab is hidden (U1's presence `away` and the document hidden) — a
//     visible console says it itself (dialog, drop, title).
//   · ONLY what U1's delivery policy would surface: an alert U1 kept as unread (never the
//     operator's own, never one already read), at most 10 min old (brief §6 inv. 3).
//   · ONE per alert id; a burst within 3 s is ONE notification (same `tag`, replaced in
//     place), the worst leading, «y N más» — U1's grouping. Two hidden tabs of the same
//     operator post the same tag, so the OS shows it once.
//   · Click → the tab is focused and goes where the lead leads.
// Degrades silently where `Notification` is missing, denied, or throws on construction
// (Android Chrome only notifies through a service worker).

/** Per-browser memory of the opt-in. */
export const OPT_IN_KEY = 'operator-ui:os-notify';
/** Render-once memory for OS notifications (bounded). */
const SHOWN_CAP = 200;
const ICON = '/nexa-favicon.svg';

export type OsPermission = NotificationPermission | 'unsupported';

type NotificationCtor = typeof Notification;

const ctorOf = (win: Window): NotificationCtor | null =>
  (win as Window & { Notification?: NotificationCtor }).Notification ?? null;

export function permissionOf(win: Window = window): OsPermission {
  try {
    return ctorOf(win)?.permission ?? 'unsupported';
  } catch {
    return 'unsupported';
  }
}

function storedOptIn(): boolean {
  try {
    return window.localStorage.getItem(OPT_IN_KEY) === 'on';
  } catch {
    return false;
  }
}

function storeOptIn(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(OPT_IN_KEY, 'on');
    else window.localStorage.removeItem(OPT_IN_KEY);
  } catch {
    // no storage: the choice lasts this page only
  }
}

export interface IOsNotifyState {
  optIn: boolean;
  permission: OsPermission;
}

export const useOsNotifyStore = create<IOsNotifyState>(() => ({
  optIn: storedOptIn(),
  permission: permissionOf(),
}));

/** Re-read both (the bell opening): the permission may have changed in the browser. */
export function refreshOsNotify(win: Window = window): void {
  useOsNotifyStore.setState({ optIn: storedOptIn(), permission: permissionOf(win) });
}

/**
 * The toggle's click. Turning it on asks the browser once if it has not answered yet —
 * synchronously inside the click, which is what lets the browser show its prompt.
 */
export async function setOsNotify(on: boolean, win: Window = window): Promise<void> {
  const N = ctorOf(win);
  let permission = permissionOf(win);
  if (on && N && permission === 'default') {
    try {
      // an old Safari answers through a callback and returns nothing
      permission = (await N.requestPermission()) ?? permissionOf(win);
    } catch {
      permission = permissionOf(win);
    }
  }
  const optIn = on && permission === 'granted';
  storeOptIn(optIn);
  useOsNotifyStore.setState({ optIn, permission });
}

export interface IOsNotifyDeps {
  /** The notification's words for a burst: its lead and how many more. */
  say(lead: IOperatorAlertV1, more: number): { title: string; body: string };
  /** Clicked: the tab is already focused — go where the lead leads. */
  go(lead: IOperatorAlertV1): void;
  /** The signed-in operator (their own actions never notify). */
  me(): string | undefined;
  win?: Window;
}

/** Watch U1's alerts store and notify the OS per the rules above. Returns the teardown. */
export function watchOsNotify(deps: IOsNotifyDeps): () => void {
  const win = deps.win ?? window;
  const shown: string[] = [];
  let burst: { tag: string; at: number; alerts: IOperatorAlertV1[] } | null = null;

  const show = (alert: IOperatorAlertV1, now: number): void => {
    const N = ctorOf(win);
    if (!N || !useOsNotifyStore.getState().optIn || permissionOf(win) !== 'granted') return;
    const hidden =
      win.document.visibilityState === 'hidden' && usePresenceStore.getState().presence === 'away';
    if (!hidden || shown.includes(alert.id)) return;
    const me = deps.me();
    if (now - Date.parse(alert.at) > DIALOG_MAX_AGE_MS || (me && alert.actorUrn === me)) return;
    shown.push(alert.id);
    if (shown.length > SHOWN_CAP) shown.shift();
    if (!burst || now - burst.at > GROUP_MS)
      burst = { tag: `nexa-alerts:${alert.id}`, at: now, alerts: [] };
    burst.alerts = [...burst.alerts, alert].sort(
      (x, y) => SEVERITY_RANK[y.severity] - SEVERITY_RANK[x.severity],
    );
    const lead = burst.alerts[0]!;
    const { title, body } = deps.say(lead, burst.alerts.length - 1);
    try {
      const n = new N(title, { body, tag: burst.tag, icon: ICON });
      n.onclick = () => {
        win.focus();
        deps.go(lead);
        n.close();
      };
    } catch {
      // no page-level notifications here: silently none
    }
  };

  return useAlertsStore.subscribe((s, prev) => {
    if (s.entries === prev.entries) return;
    const before = new Set(prev.entries.map((e) => e.alert.id));
    const now = Date.now();
    // oldest first, so a burst's notification is built in arrival order
    for (const e of [...s.entries].reverse()) {
      if (e.unread && !before.has(e.alert.id)) show(e.alert, now);
    }
  });
}
